import { createStore, createHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

export type MentionType = {
	type: 'deleted' | 'added';
	localId: string;
	id: string;
	displayName?: string;
	taskLocalId?: string;
	userPhoto?: string;
};

type MentionsNotificationsState = {
	deletedMentions: MentionType[];
	addedMentions: MentionType[];
	deletedTasks: MentionType[];
	addedTasks: MentionType[];
	unrestrictedMentions: MentionType[];
	parentSpaceRestrictedMentions: MentionType[];
	pageRestrictedMentions: MentionType[];
	unrestrictedTasks: MentionType[];
	parentSpaceRestrictedTasks: MentionType[];
	pageRestrictedTasks: MentionType[];
};

export type HandleMentionsChangedProps = {
	mentionChanges: MentionType[];
	deletedMentions: MentionType[];
	addedMentions: MentionType[];
	setDeletedMentions: (mentions: MentionType[]) => void | Promise<void>;
	setAddedMentions: (mentions: MentionType[]) => void | Promise<void>;
	userId: string | null;
	unrestrictedMentions: MentionType[];
	parentSpaceRestrictedMentions: MentionType[];
	pageRestrictedMentions: MentionType[];
	unrestrictedTasks: MentionType[];
	addedTasks: MentionType[];
	deletedTasks: MentionType[];
	setAddedTasks: (mentions: MentionType[]) => void | Promise<void>;
	setDeletedTasks: (mentions: MentionType[]) => void | Promise<void>;
	pageRestrictedTasks: MentionType[];
	parentSpaceRestrictedTasks: MentionType[];
};

const initialState: MentionsNotificationsState = {
	deletedMentions: [],
	addedMentions: [],
	deletedTasks: [],
	addedTasks: [],
	unrestrictedMentions: [], //Added mentions which have access to the current page
	parentSpaceRestrictedMentions: [], //Added mentions which dont have access to the current page due to parent or space restrictions
	pageRestrictedMentions: [], //Added mentions which dont have access to the current page due to page restrictions
	unrestrictedTasks: [], //Added task mentions which have access to the current page
	parentSpaceRestrictedTasks: [], //Added task mentions which dont have access to the current page due to parent or space restrictions
	pageRestrictedTasks: [], //Added task mentions which dont have access to the current page due to page restrictions
};

const actions = {
	setDeletedMentions:
		(mentions: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				deletedMentions: mentions,
			});
		},
	setAddedMentions:
		(mentions: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				addedMentions: mentions,
			});
		},
	setUnrestrictedMentions:
		(mentions: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				unrestrictedMentions: mentions,
			});
		},
	addUnrestrictedMentions:
		(addedUnrestrictedMentions: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const { unrestrictedMentions } = getState();
			const updatedUnrestrictedMentions = [...unrestrictedMentions, ...addedUnrestrictedMentions];
			setState({
				unrestrictedMentions: updatedUnrestrictedMentions,
			});
		},
	removeUnrestrictedMentions:
		(removedUnrestrictedMention: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(removedUnrestrictedMention.map((mention) => mention.localId));
			const { unrestrictedMentions } = getState();
			const updatedUnrestrictedMentions = unrestrictedMentions.filter(
				(mention) => !removedLocalIds.has(mention.localId),
			);
			setState({
				unrestrictedMentions: updatedUnrestrictedMentions,
			});
		},
	setParentSpaceRestrictedMentions:
		(mentions: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				parentSpaceRestrictedMentions: mentions,
			});
		},
	addParentSpaceRestrictedMentions:
		(addedParentSpaceRestrictedMentions: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const { parentSpaceRestrictedMentions } = getState();
			const updatedParentSpaceRestrictedMentions = [
				...parentSpaceRestrictedMentions,
				...addedParentSpaceRestrictedMentions,
			];
			setState({
				parentSpaceRestrictedMentions: updatedParentSpaceRestrictedMentions,
			});
		},
	removeParentSpaceRestrictedMentions:
		(removedParentSpaceRestrictedMentions: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(
				removedParentSpaceRestrictedMentions.map((mention) => mention.localId),
			);
			const { parentSpaceRestrictedMentions } = getState();
			const updatedParentSpaceRestrictedMentions = parentSpaceRestrictedMentions.filter(
				(mention) => !removedLocalIds.has(mention.localId),
			);
			setState({
				parentSpaceRestrictedMentions: updatedParentSpaceRestrictedMentions,
			});
		},
	setPageRestrictedMentions:
		(mentions: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				pageRestrictedMentions: mentions,
			});
		},
	addPageRestrictedMentions:
		(addedPageRestrictedMentions: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const { pageRestrictedMentions } = getState();
			const updatedPageRestrictedMention = [
				...pageRestrictedMentions,
				...addedPageRestrictedMentions,
			];
			setState({
				pageRestrictedMentions: updatedPageRestrictedMention,
			});
		},
	removePageRestrictedMentions:
		(removedPageRestrictedMentions: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(
				removedPageRestrictedMentions.map((mention) => mention.localId),
			);
			const { pageRestrictedMentions } = getState();
			const updatedPageRestrictedMention = pageRestrictedMentions.filter(
				(mention) => !removedLocalIds.has(mention.localId),
			);
			setState({
				pageRestrictedMentions: updatedPageRestrictedMention,
			});
		},
	setDeletedTasks:
		(tasks: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				deletedTasks: tasks,
			});
		},
	setAddedTasks:
		(tasks: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				addedTasks: tasks,
			});
		},
	setPageRestrictedTasks:
		(tasks: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				pageRestrictedTasks: tasks,
			});
		},
	addPageRestrictedTasks:
		(addedPageRestrictedTasks: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const { pageRestrictedTasks } = getState();
			const updatedPageRestrictedTasks = [...pageRestrictedTasks, ...addedPageRestrictedTasks];
			setState({
				pageRestrictedTasks: updatedPageRestrictedTasks,
			});
		},
	removePageRestrictedTasks:
		(removedPageRestrictedTasks: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(removedPageRestrictedTasks.map((tasks) => tasks.localId));
			const { pageRestrictedTasks } = getState();
			const updatedPageRestrictedTasks = pageRestrictedTasks.filter(
				(tasks) => !removedLocalIds.has(tasks.localId),
			);
			setState({
				pageRestrictedTasks: updatedPageRestrictedTasks,
			});
		},
	setParentSpaceRestrictedTasks:
		(tasks: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				parentSpaceRestrictedTasks: tasks,
			});
		},
	addParentSpaceRestrictedTasks:
		(addedParentSpaceRestrictedTasks: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const { parentSpaceRestrictedTasks } = getState();
			const updatedParentSpaceRestrictedTasks = [
				...parentSpaceRestrictedTasks,
				...addedParentSpaceRestrictedTasks,
			];
			setState({
				parentSpaceRestrictedTasks: updatedParentSpaceRestrictedTasks,
			});
		},
	removeParentSpaceRestrictedTasks:
		(removedParentSpaceRestrictedTasks: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(
				removedParentSpaceRestrictedTasks.map((tasks) => tasks.localId),
			);
			const { parentSpaceRestrictedTasks } = getState();
			const updatedParentSpaceRestrictedTasks = parentSpaceRestrictedTasks.filter(
				(tasks) => !removedLocalIds.has(tasks.localId),
			);
			setState({
				parentSpaceRestrictedTasks: updatedParentSpaceRestrictedTasks,
			});
		},
	setUnrestrictedTasks:
		(tasks: MentionType[]): Action<typeof initialState> =>
		({ setState }) => {
			setState({
				unrestrictedTasks: tasks,
			});
		},
	addUnrestrictedTasks:
		(addedUnrestrictedTasks: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const { unrestrictedTasks } = getState();
			const updatedUnrestrictedTasks = [...unrestrictedTasks, ...addedUnrestrictedTasks];
			setState({
				unrestrictedTasks: updatedUnrestrictedTasks,
			});
		},
	removeUnrestrictedTasks:
		(removedUnrestrictedTasks: MentionType[]): Action<typeof initialState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(removedUnrestrictedTasks.map((tasks) => tasks.localId));
			const { unrestrictedTasks } = getState();
			const updatedUnrestrictedTasks = unrestrictedTasks.filter(
				(tasks) => !removedLocalIds.has(tasks.localId),
			);
			setState({
				unrestrictedTasks: updatedUnrestrictedTasks,
			});
		},
};

const MentionsNotificationsStore = createStore({
	initialState,
	actions,
	name: 'MentionsNotificationsStore',
});

export const useMentionsNotifications = createHook(MentionsNotificationsStore);
